// ABOUTME: Star/Triangle/Storm tally item — the icon button + count badge, with a bilingual
// ABOUTME: text label underneath under the nalu-v1 theme, matching the Keiki Dashboard mockup.

import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { naluV1TallyLabelText } from '../theme/tokens';
import BehaviorCountBadge from './BehaviorCountBadge';
import BehaviorIconButton from './BehaviorIconButton';
import { BilingualLabel } from './BilingualLabel';
import { useEffectiveLabelLanguageMode } from '../hooks/useEffectiveLabelLanguageMode';
import { getBehaviorLabel, parseBehaviorType } from '../utils/behaviorUtils';
import type { BilingualTerm } from '../utils/bilingualGlossary';

interface BehaviorTallyButtonProps {
  behaviorType: 'star' | 'triangle' | 'square';
  icon: React.ElementType;
  count: number;
  onClick: () => void;
  size?: 'default' | 'compact';
  // Lets the caption wrap to two lines inside the tile width at phone width (crossings dashboard).
  wrapCaption?: boolean;
}

const BADGE_COLOR: Record<BehaviorTallyButtonProps['behaviorType'], 'success' | 'warning' | 'error'> = {
  star: 'success',
  triangle: 'warning',
  square: 'error',
};

const GLOSSARY_TERM: Record<BehaviorTallyButtonProps['behaviorType'], BilingualTerm> = {
  star: 'star',
  triangle: 'triangle',
  square: 'storm',
};

const BehaviorTallyButton: React.FC<BehaviorTallyButtonProps> = ({ behaviorType, icon, count, onClick, size, wrapCaption = false }) => {
  const theme = useTheme();
  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;
  const labelMode = useEffectiveLabelLanguageMode();

  const button = (
    <BehaviorCountBadge count={count} color={BADGE_COLOR[behaviorType]}>
      <BehaviorIconButton behaviorType={behaviorType} icon={icon} onClick={onClick} size={size} />
    </BehaviorCountBadge>
  );

  if (!isNaluV1) {
    return button;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      {button}
      <Box
        sx={{
          fontSize: '13px',
          fontWeight: 700,
          color: naluV1TallyLabelText,
          whiteSpace: 'nowrap',
          ...(wrapCaption && {
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            textAlign: 'center',
            maxWidth: { xs: 60, sm: 'none' },
          }),
        }}
      >
        <BilingualLabel
          term={GLOSSARY_TERM[behaviorType]}
          english={getBehaviorLabel(parseBehaviorType(behaviorType))}
          mode={labelMode ?? 'english-lead'}
        />
      </Box>
    </Box>
  );
};

export default BehaviorTallyButton;
